import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { BoardContactsUpdateModel } from 'api/client'
import { IEmailAttachment } from 'Modules/Communication/Email/shared/models/Email'
import { IMessageRecipient } from 'Modules/Communication/shared/models/Inbox'
import {
  ILoadingMessageRecipient,
  IMessageReciept,
  IMessageRecieptRecipient,
  SendEmailFieldName,
  SendMessageStateEnum,
  SendMessageStatusEnum,
} from 'shared/models/Message'
import { initialSendEmailState } from '../shared/constants'
import { SendEmailDraft } from '../shared/SendMessage'

type OpenEmailPayload = { message?: SendEmailDraft; draftId?: string }

type RemovedEmailRecipient = Partial<
  Pick<IMessageRecipient, 'nameregisterId' | 'email' | 'recipientType'>
>

const sendEmailSlice = createSlice({
  name: 'sendEmail',
  initialState: initialSendEmailState,
  reducers: {
    openEmailCompose: (state, action: PayloadAction<OpenEmailPayload>) => {
      state.state = SendMessageStateEnum.send
      state.message = action.payload.message || null
      state.messageId = action.payload.draftId || null
      state.isEditDraft = !!action.payload.draftId
    },
    closeEmail: (state) => {
      state.state = null
    },
    setEmailCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isCollapsed = action.payload
    },
    emailSendStarted: (state) => {
      state.processing = true
    },
    emailSent: (state) => {
      state.processing = false
      state.state = SendMessageStateEnum.reciept
    },
    emailSendFailed: (state, action: PayloadAction<string>) => {
      state.processing = false
      state.error = action.payload
    },
    emailReplyOrForwardStarted: (state) => {
      state.state = SendMessageStateEnum.send
      state.loading = true
    },
    emailReplyOrForwardLoaded: (
      state,
      action: PayloadAction<SendEmailDraft>
    ) => {
      state.loading = false
      state.message = action.payload
      state.messageId = action.payload.id
    },
    emailReplyOrForwardFailed: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    emailDraftCreated: (
      state,
      action: PayloadAction<{ id: string; message: SendEmailDraft }>
    ) => {
      state.message = action.payload.message
      state.messageId = action.payload.id
    },
    emailDraftUpdateStarted: (state) => {
      state.isDraftUpdated = true
    },
    emailDraftUpdated: (
      state,
      action: PayloadAction<{ field: SendEmailFieldName; value: string }>
    ) => {
      if (state.message)
        state.message[action.payload.field] = action.payload.value
    },
    emailAttachmentsSaveStarted: (state) => {
      state.savingAttachments = true
    },
    emailAttachmentsSaved: (
      state,
      action: PayloadAction<{
        allFiles: Partial<IEmailAttachment>[]
        invalidUploaded: boolean
      }>
    ) => {
      state.savingAttachments = false
      state.isDraftUpdated = true
      state.message = { ...state.message, attachments: action.payload.allFiles }
      state.error = action.payload.invalidUploaded
        ? 'En eller flere av filtypene du lastet opp støttes ikke av sikkerhetsmessige årsaker'
        : null
    },
    emailAttachmentsSaveFailed: (state, action: PayloadAction<string>) => {
      state.savingAttachments = false
      state.error = action.payload
    },
    emailReceiptRequested: (state, action: PayloadAction<string>) => {
      state.reciept.loading = true
      state.reciept.error = null
      state.reciept.messageId = action.payload
    },
    emailReceiptLoaded: (state, action: PayloadAction<IMessageReciept>) => {
      state.reciept.data = action.payload
      state.reciept.loading = false
    },
    emailReceiptFailed: (state, action: PayloadAction<string>) => {
      state.reciept.loading = false
      state.reciept.error = action.payload
    },
    emailSendingSucceeded: (state) => {
      if (state.reciept.data)
        state.reciept.data.sendingStatus = SendMessageStatusEnum.Sent
    },
    emailSendingFailed: (state, action: PayloadAction<string>) => {
      state.reciept.loading = false
      state.reciept.error = action.payload
      if (state.reciept.data)
        state.reciept.data.sendingStatus = SendMessageStatusEnum.Error
    },
    emailSendingStatusRefetched: (
      state,
      action: PayloadAction<IMessageReciept['sendingStatus']>
    ) => {
      if (state.reciept.data) state.reciept.data.sendingStatus = action.payload
    },
    setEmailCreateContact: (
      state,
      action: PayloadAction<IMessageRecieptRecipient | null>
    ) => {
      state.reciept.createContact = action.payload
      state.reciept.error = null
    },
    emailContactCreateStarted: (state) => {
      state.reciept.processing = true
    },
    emailContactCreated: (
      state,
      action: PayloadAction<BoardContactsUpdateModel>
    ) => {
      state.reciept.createContact = null
      state.reciept.processing = false

      const updated = state.reciept.data?.recipients?.find(
        (x) => x.email === action.payload.email
      )
      if (updated) updated.isAdded = true
    },
    emailContactCreateFailed: (state, action: PayloadAction<string>) => {
      state.reciept.processing = false
      state.reciept.error = action.payload
    },
    emailRecipientAdded: (
      state,
      action: PayloadAction<Partial<IMessageRecipient>>
    ) => {
      state.isDraftUpdated = true

      if (!state.message.recipients?.length) {
        state.message.recipients = [action.payload]
        return
      }

      const existing = state.message.recipients.find(
        (x) => x.email?.toLowerCase() === action.payload.email?.toLowerCase()
      )
      if (existing && existing.recipientType === action.payload.recipientType)
        return

      state.message.recipients.push(action.payload)
    },
    emailRecipientsAdded: (
      state,
      action: PayloadAction<Partial<IMessageRecipient>[]>
    ) => {
      state.isDraftUpdated = true

      if (!state.message.recipients?.length) {
        state.message.recipients = action.payload
        return
      }

      const current = state.message.recipients
      const fresh = action.payload.filter(
        (x) =>
          !current.some(
            (y) =>
              (y.nameregisterId === x.nameregisterId ||
                (!y.nameregisterId && y.email === x.email)) &&
              y.recipientType === x.recipientType
          )
      )

      state.message.recipients = [...current, ...fresh]
    },
    emailRecipientRemoved: (
      state,
      action: PayloadAction<RemovedEmailRecipient>
    ) => {
      state.isDraftUpdated = true
      state.message.recipients = state.message.recipients?.filter(
        (x) =>
          x.nameregisterId !== action.payload.nameregisterId ||
          x.recipientType !== action.payload.recipientType ||
          (x.email !== action.payload.email && Boolean(x.email))
      )
    },
    emailRecipientsLoadingStarted: (
      state,
      action: PayloadAction<ILoadingMessageRecipient[]>
    ) => {
      state.recipientLoadingQueue = [
        ...(state.recipientLoadingQueue ?? []),
        ...action.payload,
      ]
    },
    emailRecipientsLoadingFinished: (
      state,
      action: PayloadAction<ILoadingMessageRecipient[]>
    ) => {
      state.recipientLoadingQueue = state.recipientLoadingQueue?.filter(
        (x) =>
          !action.payload.some(
            (y) =>
              y.id === x.id &&
              y.type === x.type &&
              y.recipientType === x.recipientType
          )
      )
    },
    resetEmail: () => initialSendEmailState,
  },
})

export default sendEmailSlice
